/**
 * Credit Estimate Service
 * Provides credit estimation for various scenarios
 */

import { api } from './api'

export enum CreditEstimateScenario {
  // Prompt Tracking
  PROMPT_GENERATION = 'prompt_generation',
  PROMPT_TRACKING = 'prompt_tracking',

  // Content Studio
  CONTENT_STUDIO_TOPIC_SUGGESTION = 'content_studio_topic_suggestion',
  CONTENT_STUDIO_GENERATE_CONTENT = 'content_studio_generate_content',
}

export interface CreditEstimateParams {
  scenario: CreditEstimateScenario
  params: Record<string, any>
  required_fraction?: number
}

export interface CreditEstimateResult {
  scenario: string
  estimated_min_credits: number
  estimated_max_credits: number
  required_fraction: number
  required_minimum_balance: number
  breakdown?: Record<string, any>
}

export interface BalanceCheckResult {
  sufficient: boolean
  current_balance: number
  required_balance: number
  shortfall: number
}

/**
 * Estimate credits for a given scenario
 */
export async function estimateCredits(
  params: CreditEstimateParams
): Promise<CreditEstimateResult> {
  const response = await api.creditEstimate.estimate(params)
  return response
}

/**
 * Check if user has sufficient balance for a scenario
 */
export async function checkBalance(
  scenario: CreditEstimateScenario,
  params: Record<string, any>
): Promise<BalanceCheckResult> {
  const response = await api.creditEstimate.checkBalance({ scenario, params })
  return response
}

/**
 * Format credits for display (e.g., "~1,234" or "~1.2K")
 */
export function formatEstimatedCredits(min: number, max: number): string {
  if (min === max) {
    return `~${formatNumber(max)}`
  }
  return `~${formatNumber(min)}-${formatNumber(max)}`
}

function formatNumber(num: number): string {
  if (num >= 10000) {
    return `${(num / 1000).toFixed(0)}K`
  }
  if (num >= 1000) {
    return num.toLocaleString()
  }
  return num.toString()
}
